import { isAxiosError } from "axios";

/**
 * Retry count and initial delay configuration for transient failures.
 */
export interface RetryOptions {
  maxRetries?: number;
  baseDelayMs?: number;
}

/**
 * Repeats a transiently failing request with bounded exponential backoff.
 *
 * @param operation - Request to retry
 * @param signal - Optional signal used to cancel the request or backoff delay
 * @param options - Retry count and initial delay overrides for callers and tests
 * @returns The successful operation result
 */
export const retryWithBackoff = async <T>(
  operation: () => Promise<T>,
  signal?: AbortSignal,
  options: RetryOptions = {},
): Promise<T> => {
  const maxRetries = Math.max(0, Math.min(options.maxRetries ?? 3, 3));
  const baseDelayMs = Math.max(0, options.baseDelayMs ?? 250);

  for (let attempt = 0; ; attempt += 1) {
    if (signal?.aborted) {
      throw new DOMException("The request was cancelled", "AbortError");
    }

    try {
      return await operation();
    } catch (error: unknown) {
      const status = isAxiosError(error) ? error.response?.status : undefined;
      const transient =
        isAxiosError(error) &&
        error.code !== "ERR_CANCELED" &&
        (status === undefined ||
          status === 408 ||
          status === 429 ||
          (status >= 500 && status < 600));

      if (!transient || attempt >= maxRetries) {
        throw error;
      }

      await new Promise<void>((resolve, reject) => {
        if (signal?.aborted) {
          reject(new DOMException("The request was cancelled", "AbortError"));
          return;
        }

        const timer = setTimeout(
          () => {
            signal?.removeEventListener("abort", abortDelay);
            resolve();
          },
          baseDelayMs * 2 ** attempt,
        );
        /**
         * Reject the active delay when cancellation arrives.
         */
        const abortDelay = () => {
          clearTimeout(timer);
          signal?.removeEventListener("abort", abortDelay);
          reject(new DOMException("The request was cancelled", "AbortError"));
        };
        signal?.addEventListener("abort", abortDelay, { once: true });
      });
    }
  }
};
